import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Loader from '@/components/ui/loader/Loader';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IStoreForOAuth } from '@/interfaces/api/IStore';
import { InventoryType } from '@/interfaces/api/InventoryType';
import {
	OAuthState,
	StateStatus,
	StateType,
} from '@/interfaces/auth/OAuthState.enum';
import { StoredCookies } from '@/interfaces/auth/cookies.enum';
import { cookieService } from '@/services/cookie.service';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

export default function ShopifyOauth() {
	const [flowState, setFlowState] = useState<OAuthState>({
		status: StateStatus.LOADING,
	});
	const [searchParams, setSearchParams] = useSearchParams();
	const [countdown, setCountdown] = useState<number | null>(null);
	const isCreatingStore = useRef(false);
	const hasShownNotification = useRef(false);

	const { user } = useUser();

	const { data: storeResponse, isLoading: userStoreIsLoading } =
		useQuery<IStoreForOAuth>({
			queryKey: [ApiQueryKeys.userStore, 'oauth'],
			queryFn: () => haggleApiService.getUserStoreForOAuth(true),
			enabled: !!(
				user &&
				!user.roles.includes('admin') &&
				!user.roles.includes('consumer')
			),
			retry: false,
			refetchOnWindowFocus: false,
		});

	const [storeParams, setStoreParams] = useState<{
		shopName: string | null;
		shopAt: string | null;
	}>({
		shopName: null,
		shopAt: null,
	});

	useEffect(() => {
		const shopName = searchParams.get('shop');
		const shopAt = searchParams.get('shopat');

		if (shopName && shopAt) {
			setStoreParams({
				shopName,
				shopAt,
			});

			setSearchParams({}, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	useEffect(() => {
		if (!userStoreIsLoading && storeResponse?.store && storeResponse.store.id) {
			cookieService.remove(StoredCookies.REDIRECT_QUERY_PARAMS);

			if (user && user.inventoryType === InventoryType.CSV) {
				setFlowState({
					status: StateStatus.SUCCESS,
					type: StateType.EXISTING_CSV,
				});
			} else {
				setFlowState({
					status: StateStatus.SUCCESS,
					type: StateType.EXISTING_SHOPIFY,
				});
			}
		} else if (
			storeParams.shopName &&
			storeParams.shopAt &&
			!isCreatingStore.current &&
			!hasShownNotification.current
		) {
			isCreatingStore.current = true;
			cookieService.remove(StoredCookies.REDIRECT_QUERY_PARAMS);
			saveStoreAndRedirect(storeParams.shopName, storeParams.shopAt)
				.then(async () => {
					await haggleApiService.updateInventoryType({
						inventoryType: InventoryType.SHOPIFY,
					});
					console.log('Store created successfully');
					if (!hasShownNotification.current) {
						notificationService.success('Store created successfully');
						hasShownNotification.current = true;
					}
					setFlowState({
						status: StateStatus.SUCCESS,
						type: StateType.NEW_STORE,
					});
					setCountdown(3);

					if (window.opener) {
						window.opener.postMessage(
							{
								success: true,
								shopName: storeParams.shopName,
								message: 'Store connected successfully',
							},
							window.location.origin,
						);
					}
				})
				.catch((errorMessage) => {
					const isStoreExistsError = errorMessage.includes(
						'Already exists a store for this user',
					);
					console.log('errorMessage', isStoreExistsError);

					if (!hasShownNotification.current) {
						if (isStoreExistsError) {
							notificationService.success(
								'You already have a store connected.',
							);
						} else {
							notificationService.error(errorMessage);
						}
						hasShownNotification.current = true;
					}

					if (isStoreExistsError) {
						if (user && user.inventoryType === InventoryType.CSV) {
							setFlowState({
								status: StateStatus.SUCCESS,
								type: StateType.EXISTING_CSV,
							});
						} else {
							setFlowState({
								status: StateStatus.SUCCESS,
								type: StateType.EXISTING_SHOPIFY,
							});
						}
						setCountdown(3);

						if (window.opener) {
							window.opener.postMessage(
								{
									success: true,
									shopName: storeParams.shopName,
									message: 'Store already connected',
									isExisting: true,
								},
								window.location.origin,
							);
						}
					} else if (window.opener) {
						window.opener.postMessage(
							{
								success: false,
								error: errorMessage,
							},
							window.location.origin,
						);
					} else {
						setFlowState({ status: StateStatus.ERROR, error: errorMessage });
					}
				})
				.finally(() => {
					isCreatingStore.current = false;
				});
		}
	}, [storeResponse, userStoreIsLoading, storeParams, user]);

	const saveStoreAndRedirect = async (shopName: string, shopAt: string) => {
		return new Promise((resolve, reject) => {
			if (shopName !== '' && shopAt !== '') {
				haggleApiService
					.createStore(shopName, shopName, shopAt)
					.then(async (message) => {
						resolve(message);
					})
					.catch((error) => {
						console.log(error);
						reject(error.message);
					});
			} else {
				reject('Invalid shop name or access token');
			}
		});
	};

	useEffect(() => {
		if (countdown !== null) {
			if (countdown <= 0) {
				window.close();
				return;
			}

			const timer = setTimeout(() => {
				setCountdown((prev) => (prev !== null ? prev - 1 : null));
			}, 1000);

			return () => clearTimeout(timer);
		}
	}, [countdown]);

	return (
		<div data-test="shopify-oauth-page" className="h-full">
			{flowState.status === 'loading' && (
				<div className="flex flex-col items-center justify-center w-full h-full p-4">
					<Loader
						className="flex justify-center w-full p-4 mb-4"
						variant="scaleUp"
						size="large"
						showOnlyThreeDots={false}
						data-test="loader"
					/>
					<p className="text-lg text-gray-700">
						Connecting your Shopify store...
					</p>
				</div>
			)}

			{flowState.status === 'success' && (
				<div className="flex flex-col items-center justify-center w-full h-full">
					<div className="text-center p-8 max-w-md bg-white rounded-lg shadow-md">
						<h2 className="text-2xl font-bold text-gray-800 mb-4">
							{flowState.type === 'new-store' &&
								'Store Connected Successfully!'}
							{flowState.type === 'existing-shopify' &&
								'Store Already Connected!'}
							{flowState.type === 'existing-csv' &&
								'CSV Store Already Connected!'}
						</h2>
						<p className="text-gray-600 mb-6">
							{flowState.type === 'new-store' &&
								'Your Shopify store has been successfully connected.'}
							{flowState.type === 'existing-shopify' &&
								'Your store is already connected to your account.'}
							{flowState.type === 'existing-csv' &&
								'Your CSV store is already connected to your account.'}
						</p>

						{countdown !== null && (
							<p className="text-amber-600 mb-4">
								This window will close in {countdown} second
								{countdown !== 1 ? 's' : ''}...
							</p>
						)}
						<button
							onClick={() => window.close()}
							className="bg-purpleBrand-500 hover:bg-purpleBrand-600 text-white px-6 py-2 rounded-md"
						>
							Close Window
						</button>
					</div>
				</div>
			)}

			{flowState.status === 'error' && (
				<div className="flex flex-col items-center justify-center w-full h-full">
					<div className="text-center p-8 max-w-md bg-white rounded-lg shadow-md">
						<h2 className="text-2xl font-bold text-gray-800 mb-4">
							Store Connection Failed
						</h2>
						<p className="text-gray-600 mb-6">
							{flowState.error === 'CSV store already connected'
								? 'You already have a CSV store connected to your account. Please contact support if you need help.'
								: flowState.error ||
								  'Please try connecting your store again or contact support.'}
						</p>
					</div>
				</div>
			)}
		</div>
	);
}
